import { userInfoInterface } from 'Interfaces/User/userInterfaces';
import {
  setAccessToken,
  setRefreshToken,
  setTokenExpiryTime,
  tokenTypes,
} from 'utils/AuthUtils/tokenUtils';
import { computed, makeAutoObservable } from 'mobx';
import { authServices } from 'services/authServices';
import {
  departmentTypes,
  moduleTypes,
  roleInstance,
  roleTypes,
} from 'Interfaces/roleBasedAccessInterfaces';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import SentryService from 'services/sentryService';

export class UserStore {
  userMetadata: userInfoInterface | null = null;
  isUserInfoLoading: boolean = false;
  isLoginLoading: boolean = false;
  isAuthenticated: boolean = localStorage.getItem(tokenTypes.access) ? true : false;
  userModulesRoles: { [key in moduleTypes]: roleInstance[] } = {
    facts: [],
    fdp: [],
    opptracker: [],
    plm: [],
  };

  constructor() {
    makeAutoObservable(this);
  }

  toggleLoginLoading = () => {
    this.isLoginLoading = !this.isLoginLoading;
  };

  setIsAuthenticated = (state: boolean) => {
    this.isAuthenticated = state;
  };

  handleLogout = () => {
    setAccessToken('');
    setRefreshToken('');
    setTokenExpiryTime('');
    this.isAuthenticated = false;
    Sentry.setUser(null);
  };

  @computed isUserLoggedIn = () => {
    if (localStorage.getItem(tokenTypes.access)) {
      return true;
    }
    return false;
  };

  /** Display string for user role (e.g. for support tickets). Derived from PLM roles first, then other modules. */
  get userRole(): string {
    const plm = this.userModulesRoles?.plm?.[0];
    if (plm?.role) return plm.role;
    const fdp = this.userModulesRoles?.fdp?.[0];
    if (fdp?.role) return fdp.role;
    const facts = this.userModulesRoles?.facts?.[0];
    if (facts?.role) return facts.role;
    const opp = this.userModulesRoles?.opptracker?.[0];
    if (opp?.role) return opp.role;
    return '';
  }

  fetchUserDetails = async () => {
    try {
      this.isUserInfoLoading = true;
      this.userMetadata = await authServices.getUserInfoAndRoles();
      const tempUserRoles: { [key in moduleTypes]: roleInstance[] } = {
        facts: [],
        fdp: [],
        opptracker: [],
        plm: [],
      };
      if (this.userMetadata) {
        this.userMetadata.roles.facts.forEach(role => {
          if (role)
            tempUserRoles.facts.push({
              role: role.roleId.roleCode as roleTypes,
              department: this.userMetadata?.userInfo.deptId.departmentCode as departmentTypes,
            });
        });

        this.userMetadata.roles.fdp.forEach(role => {
          tempUserRoles.fdp.push({
            role: role.roleId.roleCode as roleTypes,
            department: this.userMetadata?.userInfo.deptId.departmentCode as departmentTypes,
          });
        });
        this.userMetadata.roles.opptracker.forEach(role => {
          tempUserRoles.opptracker.push({
            role: role.roleId.roleCode as roleTypes,
            department: this.userMetadata?.userInfo.deptId.departmentCode as departmentTypes,
          });
        });

        this.userMetadata.roles.plm.forEach(role => {
          tempUserRoles.plm.push({
            role: role.roleId.roleCode as roleTypes,
            department: this.userMetadata?.userInfo.deptId.departmentCode as departmentTypes,
          });
        });
      }
      this.userModulesRoles = tempUserRoles;

      // Identify user in Sentry — links every subsequent error to this person
      if (this.userMetadata) {
        SentryService.getInstance().identifyUser(this.userMetadata);
        Sentry.setTag('department', this.userMetadata.userInfo.deptId.departmentCode);
        Sentry.setTag('role', this.userRole);
      }
    } catch {
      toast.error('Some error occured while fetching the user information');
    } finally {
      this.isUserInfoLoading = false;
    }
  };
}
